import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Mail, Instagram, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Helmet } from 'react-helmet-async';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contatti = () => {
  const [searchParams] = useSearchParams();
  const consulenza = searchParams.get('consulenza');

  const getInitialMessage = () => {
    if (consulenza) {
      return `Ciao! Sono interessata/o alla ${consulenza}.\n\nDestinazone di interesse: \nPeriodo di viaggio: \nNumero e età dei bambini: \n\nGrazie!`;
    }
    return '';
  };

  const [formData, setFormData] = useState({ nome: '', email: '', messaggio: getInitialMessage() });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Messaggio inviato con successo! Ti risponderò presto.');
      setFormData({ nome: '', email: '', messaggio: '' });
    } catch (error) {
      toast.error('Errore nell\'invio del messaggio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contatti-page">
      <Helmet>
        <title>Contatti | Scrivici — Chichi in Viaggio</title>
        <meta name="description" content="Contattaci per informazioni, collaborazioni o consulenze di viaggio in famiglia con bambini. Rispondiamo a ogni messaggio." />
      </Helmet>
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="font-heading font-light text-5xl md:text-7xl tracking-tight mb-6" data-testid="contatti-title">
              {consulenza ? 'Richiedi consulenza' : 'Contatti'}
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-foreground/80 max-w-2xl mx-auto" data-testid="contatti-subtitle">
              {consulenza
                ? `Compila il form con i dettagli del tuo viaggio e ti ricontatteremo per fissare la tua ${consulenza}.`
                : 'Hai domande, proposte o semplicemente vuoi condividere la tua esperienza di viaggio? Scrivici, saremo felici di risponderti!'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="font-serif text-3xl mb-6">{consulenza ? 'Dettagli del viaggio' : 'Invia un messaggio'}</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Input
                    type="text"
                    placeholder="Il tuo nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    className="w-full"
                    data-testid="contact-form-name"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="La tua email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full"
                    data-testid="contact-form-email"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Il tuo messaggio..."
                    value={formData.messaggio}
                    onChange={(e) => setFormData({ ...formData, messaggio: e.target.value })}
                    required
                    rows={6}
                    className="w-full resize-none"
                    data-testid="contact-form-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full py-6 text-lg"
                  data-testid="contact-form-submit"
                >
                  {loading ? 'Invio in corso...' : 'Invia messaggio'}
                </Button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-serif text-3xl mb-6">Altri modi per contattarci</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Instagram className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Instagram</h3>
                      <p className="text-foreground/70 mb-2">
                        Il modo più veloce per entrare in contatto e vedere i contenuti quotidiani.
                      </p>
                      <a
                        href="https://instagram.com/chichi_inviaggio"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:text-accent/80 transition-colors"
                        data-testid="instagram-link"
                      >
                        @chichi_inviaggio
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Email diretta</h3>
                      <p className="text-foreground/70 mb-2">
                        Per collaborazioni o richieste più articolate.
                      </p>
                      <p className="text-accent">Usa il form qui accanto</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-muted p-8 rounded-2xl" data-testid="response-time-info">
                <div className="flex items-start gap-3 mb-4">
                  <MessageCircle className="text-primary flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold mb-2">Tempi di risposta</h3>
                    <p className="text-foreground/70 leading-relaxed text-sm">
                      Rispondiamo a tutti i messaggi entro 48-72 ore. Se hai urgenza, contattaci su
                      Instagram per una risposta più rapida.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent/5 border border-accent/20 p-8 rounded-2xl">
                <h3 className="font-serif text-2xl mb-3">Grazie!</h3>
                <p className="text-foreground/70 leading-relaxed">
                  Ogni messaggio che riceviamo è importante per noi. Che si tratti di una domanda, un
                  suggerimento o semplicemente un saluto, saremo felici di leggerti e risponderti.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contatti;